import { useEffect, useCallback, useState } from 'react';
import ticketWebSocketService from './websocketService';

/**
 * Custom React Hook for Ticket WebSocket Integration
 *
 * Usage:
 * const { isConnected, connectionStatus } = useTicketWebSocket({
 *   onTicketCreated: (ticket) => console.log('New ticket:', ticket),
 *   onTicketUpdated: (ticket) => console.log('Updated ticket:', ticket),
 *   onTicketDeleted: (ticketId) => console.log('Deleted ticket:', ticketId),
 * });
 */
const useTicketWebSocket = (callbacks = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  // Handle connection status changes
  const handleConnectionChange = useCallback((data) => {
    const { status } = data;
    setConnectionStatus(status);
    setIsConnected(status === 'connected');

    if (callbacks.onConnectionChange) {
      callbacks.onConnectionChange(status);
    }
  }, [callbacks]);

  // Connect to WebSocket on mount
  useEffect(() => {
    // Connect to WebSocket
    ticketWebSocketService.connect();

    // Subscribe to connection events
    const unsubscribeConnection = ticketWebSocketService.subscribe(
      'connection',
      handleConnectionChange
    );

    // Subscribe to ticket events
    const unsubscribeTicketCreated = callbacks.onTicketCreated
      ? ticketWebSocketService.subscribe('ticket_created', callbacks.onTicketCreated)
      : null;

    const unsubscribeTicketUpdated = callbacks.onTicketUpdated
      ? ticketWebSocketService.subscribe('ticket_updated', callbacks.onTicketUpdated)
      : null;

    const unsubscribeTicketDeleted = callbacks.onTicketDeleted
      ? ticketWebSocketService.subscribe('ticket_deleted', callbacks.onTicketDeleted)
      : null;

    const unsubscribeCommentAdded = callbacks.onCommentAdded
      ? ticketWebSocketService.subscribe('comment_added', callbacks.onCommentAdded)
      : null;

    const unsubscribeCommentDeleted = callbacks.onCommentDeleted
      ? ticketWebSocketService.subscribe('comment_deleted', callbacks.onCommentDeleted)
      : null;

    const unsubscribeAttachmentAdded = callbacks.onAttachmentAdded
      ? ticketWebSocketService.subscribe('attachment_added', callbacks.onAttachmentAdded)
      : null;

    const unsubscribeAttachmentDeleted = callbacks.onAttachmentDeleted
      ? ticketWebSocketService.subscribe('attachment_deleted', callbacks.onAttachmentDeleted)
      : null;

    const unsubscribeError = callbacks.onError
      ? ticketWebSocketService.subscribe('error', callbacks.onError)
      : null;

    // Update initial connection status
    setConnectionStatus(ticketWebSocketService.getStatus());
    setIsConnected(ticketWebSocketService.isConnected());

    // Cleanup on unmount
    return () => {
      unsubscribeConnection();
      if (unsubscribeTicketCreated) unsubscribeTicketCreated();
      if (unsubscribeTicketUpdated) unsubscribeTicketUpdated();
      if (unsubscribeTicketDeleted) unsubscribeTicketDeleted();
      if (unsubscribeCommentAdded) unsubscribeCommentAdded();
      if (unsubscribeCommentDeleted) unsubscribeCommentDeleted();
      if (unsubscribeAttachmentAdded) unsubscribeAttachmentAdded();
      if (unsubscribeAttachmentDeleted) unsubscribeAttachmentDeleted();
      if (unsubscribeError) unsubscribeError();
    };
  }, []); // Empty dependency array - connect once on mount

  // Send message function
  const sendMessage = useCallback((data) => {
    ticketWebSocketService.send(data);
  }, []);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    ticketWebSocketService.disconnect();
    setTimeout(() => {
      ticketWebSocketService.connect();
    }, 1000);
  }, []);

  return {
    isConnected,
    connectionStatus,
    sendMessage,
    reconnect,
  };
};

export default useTicketWebSocket;
